import { useUpdateTask } from '@/core/domain/operational/Tasks/Tasks.hooks';
import {
  UpdateTaskRequest,
  UpdateTaskRequestWithSubAreaId,
} from '@/core/domain/operational/Tasks/Tasks.types';
import { useDefaultFormErrorNotification, useServiceError } from '@/hooks';
import { Form } from 'antd';
import { AxiosError } from 'axios';
import { Props } from 'framer-motion/types/types';
import { FormChangeInfo } from 'rc-field-form/es/FormContext';
import { createContext, useContext, useEffect, useState } from 'react';
import { useRouteMatch } from 'react-router-dom';
import { useDebouncedCallback } from 'use-debounce';

interface Forms {
  generalTasks?: UpdateTaskRequest;
}

interface ContextValues {
  disabled: boolean;
  contextForm: Forms;
  isLoading: boolean;
  taskId: string;
  setActive: (isActive: boolean) => void;
  handleSubmit: () => void;
  active: boolean;
}

const TaskFormContext = createContext({} as ContextValues);
export const useTaskFormContext = () => useContext(TaskFormContext);

const TaskFormProvider: React.FC<Props> = ({ children }) => {
  const { sendFormErrorNotification } = useDefaultFormErrorNotification();
  const { params } = useRouteMatch<{ taskId: string }>();

  const [contextForm, setContextForm] = useState<Forms>({});
  const [isLoading, setIsLoading] = useState(false);
  const [taskId, setTaskId] = useState<string>('');
  const [active, setActive] = useState<boolean>(true);

  const updateTask = useUpdateTask();

  const disabled = !Object.keys(contextForm).some((key) => !!key);

  useServiceError(updateTask.error);

  useEffect(() => {
    setContextForm({});
    if (params.taskId) {
      setTaskId(params.taskId);
    }
  }, [params.taskId]);

  const handleFormChange = useDebouncedCallback(
    (name: string, info: FormChangeInfo) => {
      if (name && info?.forms[name]) {
        setContextForm((state) => ({
          ...state,
          [name]: info.forms[name].getFieldsValue(),
        }));
      }
    },
    250,
  );

  async function handleSubmit() {
    setIsLoading(true);
    try {
      if (contextForm.generalTasks) {
        const values = contextForm.generalTasks;

        const obj: UpdateTaskRequestWithSubAreaId = {
          ...values,
          active,
          id: +taskId,
          subArea: { id: values.subAreaId },
          taskPriceSet: values.taskPriceSet.map((item) => ({
            id: item.id,
            companyId: item.companyId,
            price: item.price,
            priceFixed: item.priceFixed,
            commissionPercentage: item.commissionPercentage,
          })),
        };

        await updateTask.mutateAsync(obj);
      }
    } catch (error: unknown) {
      const err = error as AxiosError;
      if (!err.isAxiosError) {
        sendFormErrorNotification(error);
      }
    }
    setIsLoading(false);
    setContextForm({});
  }

  return (
    <TaskFormContext.Provider
      value={{
        isLoading,
        contextForm,
        taskId,
        disabled,
        handleSubmit,
        setActive,
        active,
      }}
    >
      <Form.Provider
        onFormChange={handleFormChange}
        onFormFinish={handleSubmit}
      >
        {children}
      </Form.Provider>
    </TaskFormContext.Provider>
  );
};
export default TaskFormProvider;
